/**
 * This file defines the theme for the application, including colors, breakpoints,
 * spacing, and common styles. It is used throughout the application to maintain
 * a consistent look and feel.
 */

/**
 * Breakpoints define the screen sizes at which the application will change layout
 */
const BREAKPOINTS = {
	xs: 480,
	sm: 640,
	md: 768,
	lg: 1024,
	xl: 1280,
	xxl: 1536,
	xxxl: 1920,
	xxxxl: 2560,
} as const;

/**
 * COLORS defines the color palette used throughout the application.
 * Each color is defined using CSS variables for easy theming and consistency.
 */
const COLORS = {
	/**
	 * Background colors
	 * - main: light gray background for top level pages
	 * - light: white background for cards and other foreground elements
	 * - dark: dark gray background for labels and tags
	 */
	background: {
		main: 'var(--color-background-main)',
		light: 'var(--color-background-light)',
		dark: 'var(--color-background-dark)',
	},
	/**
	 * Border colors
	 * - main: gray used for most elements
	 * - light: light gray used for for divider elements
	 */
	border: {
		main: 'var(--color-border-main)',
		light: 'var(--color-border-light)',
	},
	/**
	 * Text colors
	 * - main: almost black, used only against white backgrounds
	 * - light: dark gray used for some headers, should never be used against tinted backgrounds
	 * - dark: black contrast text for tinted backgrounds
	 */
	text: {
		main: 'var(--color-text-main)',
		light: 'var(--color-text-light)',
		dark: 'var(--color-text-dark)',
	},
	/**
	 * Primary, Levitate blue
	 * - main: levitate branded blue used for primary buttons and links
	 * - light: used for disabled buttons and some backgrounds
	 * - dark: no dedicated use yet
	 */
	primary: {
		main: 'var(--color-primary-main)',
		light: 'var(--color-primary-light)',
		dark: 'var(--color-primary-dark)',
		contrastText: 'var(--color-text-contrast)',
	},
	/**
	 * Secondary, Presto purple
	 * - main: purple, tbd whether this will actually be used for secondary buttons
	 * - light: used for tag backgrounds
	 * - dark: no dedicated use yet
	 * - contrastText: white, should be used against main and light
	 */
	secondary: {
		main: 'var(--color-secondary-main)',
		light: 'var(--color-secondary-light)',
		dark: 'var(--color-secondary-dark)',
		contrastText: 'var(--color-text-contrast)',
	},
	/**
	 * Tertiary, Potion pink
	 * - main: pink, tbd if this will gain wider use
	 * - light: used for keyword backgrounds
	 * - dark: no dedicated use yet
	 * - contrastText: white, should be used against main and dark
	 */
	tertiary: {
		main: 'var(--color-tertiary-main)',
		light: 'var(--color-tertiary-light)',
		dark: 'var(--color-tertiary-dark)',
		contrastText: 'var(--color-text-contrast)',
	},
	/**
	 * Hover, Enchantment blue
	 * - main: bright teal use for button and link hover states
	 * - light: used for tab and select menu hover states, as well as table headers
	 * - dark: no dedicated use yet
	 * - contrastText: black, should be used against main, light and dark
	 */
	hover: {
		main: 'var(--color-hover-main)',
		light: 'var(--color-hover-light)',
		dark: 'var(--color-hover-dark)',
		contrastText: 'var(--color-text-dark)',
	},
	/**
	 * Info, Illusion yellow
	 * - main: yellow used for info toast and banner borders
	 * - light: used for tag, info toast and banner backgrounds
	 * - dark: no dedicated use yet
	 * - contrastText: black, should be used against main, light and dark
	 */
	info: {
		main: 'var(--color-info-main)',
		light: 'var(--color-info-light)',
		dark: 'var(--color-info-dark)',
		contrastText: 'var(--color-text-dark)',
	},
	/**
	 * Warning, Magic orange
	 * - main: orange used for warning toast and banner borders
	 * - light: used for tag, warning toast and banner backgrounds
	 * - dark: no dedicated use yet
	 * - contrastText: black, should be used against main, light and dark
	 */
	warning: {
		main: 'var(--color-warning-main)',
		light: 'var(--color-warning-light)',
		dark: 'var(--color-warning-dark)',
		contrastText: 'var(--color-text-dark)',
	},
	/**
	 * Success, Trickster green
	 * - main: green used for success toast and banner borders
	 * - light: used for tag, success toast and banner backgrounds
	 * - dark: no dedicated use yet
	 * - contrastText: black, should be used against main, light and dark
	 */
	success: {
		main: 'var(--color-success-main)',
		light: 'var(--color-success-light)',
		dark: 'var(--color-success-dark)',
		contrastText: 'var(--color-text-dark)',
	},
	/**
	 * Danger, Spell red
	 * - main: red used for danger toast and banner borders
	 * - light: used for tag, danger toast and banner backgrounds
	 * - dark: no dedicated use yet
	 * - contrastText: white, should be used against dark
	 */
	danger: {
		main: 'var(--color-danger-main)',
		light: 'var(--color-danger-light)',
		dark: 'var(--color-danger-dark)',
		contrastText: 'var(--color-text-contrast)',
	},
	/**
	 * Icon colors
	 * - gray: gray used for most icons
	 * - blue: levitate branded blue used for primary icons
	 * - red: red used for danger icons
	 */
	icon: {
		disabled: 'var(--color-gray-light)',
		gray: 'var(--color-gray-dark)',
		primary: 'var(--color-primary-main)',
		secondary: 'var(--color-secondary-main)',
		error: 'var(--color-danger-dark)',
	},
	/**
	 * Pill colors
	 * color themes for keywords, labels and tags
	 */
	pill: {
		gray: {
			background: 'var(--color-pill-gray-background)',
			border: 'var(--color-pill-gray-border)',
			icon: 'var(--color-pill-gray-icon)',
		},
		blue: {
			background: 'var(--color-pill-blue-background)',
			border: 'var(--color-pill-blue-border)',
			icon: 'var(--color-pill-blue-icon)',
		},
		teal: {
			background: 'var(--color-pill-teal-background)',
			border: 'var(--color-pill-teal-border)',
			icon: 'var(--color-pill-teal-icon)',
		},
		green: {
			background: 'var(--color-pill-green-background)',
			border: 'var(--color-pill-green-border)',
			icon: 'var(--color-pill-green-icon)',
		},
		yellow: {
			background: 'var(--color-pill-yellow-background)',
			border: 'var(--color-pill-yellow-border)',
			icon: 'var(--color-pill-yellow-icon)',
		},
		orange: {
			background: 'var(--color-pill-orange-background)',
			border: 'var(--color-pill-orange-border)',
			icon: 'var(--color-pill-orange-icon)',
		},
		red: {
			background: 'var(--color-pill-red-background)',
			border: 'var(--color-pill-red-border)',
			icon: 'var(--color-pill-red-icon)',
		},
		pink: {
			background: 'var(--color-pill-pink-background)',
			border: 'var(--color-pill-pink-border)',
			icon: 'var(--color-pill-pink-icon)',
		},
		purple: {
			background: 'var(--color-pill-purple-background)',
			border: 'var(--color-pill-purple-border)',
			icon: 'var(--color-pill-purple-icon)',
		},
	},
} as const;
/**
 * SPACING defines the spacing values used throughout the application.
 * These values are used for margins, paddings, and other spacing-related styles.
 * Each value is defined in pixels.
 * - none: 0px
 * - xs: 4px
 * - sm: 8px
 * - md: 16px
 * - lg: 24px
 * - xl: 32px
 * - xxl: 40px
 * - xxxl: 48px
 */
const SPACING = {
	none: 0,
	xs: 4,
	sm: 8,
	md: 16,
	lg: 24,
	xl: 32,
	xxl: 40,
	xxxl: 48,
} as const;

/** FONT_FAMILIES
 * lexend: 'Lexend, OpenSans, sans-serif'
 * sora: 'Sora, OpenSans, sans-serif'
 */
const FONT_FAMILIES = {
	lexend: 'Lexend, OpenSans, sans-serif',
	sora: 'Sora, OpenSans, sans-serif',
};

/**
 * FONT_WEIGHTS
 * standard: 400,
 * bold: 600
 */
const FONT_WEIGHTS = {
	standard: 400,
	bold: 600,
} as const;
/**
 * FONT_SIZES defines the font sizes used throughout the application.
 * These values are used for text elements to maintain consistency in typography.
 * - none: 0px
 * - xxs: 11px, used as smallest font size
 * - xs: 12px
 * - sm: 14px
 * - md: 16px
 * - lg: 18px
 * - xl: 24px
 * - xxl: 32px
 */
const FONT_SIZES = {
	none: 0,
	xxs: 11,
	xs: 12,
	sm: 14,
	md: 16,
	lg: 18,
	xl: 24,
	xxl: 32,
} as const;
/**
 * COMMON defines common styles used throughout the application.
 * These styles are used for consistency in layout and appearance.
 */
const COMMON = {
	borderRadius: 8,
	border: `1px solid ${COLORS.border.main}`,
	truncate: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	scrollBar: {
		'::-webkit-scrollbar': {
			background: 'transparent',
			width: 8,
			borderRadius: 8,
			height: 10,
		},
		'::-webkit-scrollbar-thumb': {
			backgroundColor: COLORS.border.main,
			borderRadius: 10,
			padding: '0 2px',
			width: 6,
		},
	},
} as const;

/**
 * QUERIES defines media queries for responsive design.
 * These queries are used to apply styles based on the screen size.
 */
const QUERIES = {
	xs: `@media (max-width: ${BREAKPOINTS.xs}px)`,
	sm: `@media (max-width: ${BREAKPOINTS.sm}px)`,
	md: `@media (max-width: ${BREAKPOINTS.md}px)`,
	lg: `@media (max-width: ${BREAKPOINTS.lg}px)`,
	xl: `@media (max-width: ${BREAKPOINTS.xl}px)`,
	compactHeight: '@media (max-height: 650px)',
} as const;

/**
 * THEME defines the overall theme for the application.
 * It includes colors, spacing, and common styles.
 */
export const THEME = {
	breakpoints: BREAKPOINTS,
	colors: COLORS,
	common: COMMON,
	spacing: SPACING,
	queries: QUERIES,
	typography: {
		fontFamilies: FONT_FAMILIES,
		fontSizes: FONT_SIZES,
		fontWeights: FONT_WEIGHTS,
	},
} as const;

/**
 * Type definitions for the theme
 */
export type ThemeColors = typeof COLORS;
export type ColorKeys = keyof ThemeColors;
export type MediaQueries = typeof QUERIES;
export type MediaQueryKeys = keyof typeof QUERIES;
export type Spacing = typeof SPACING;
export type SpacingKeys = keyof typeof SPACING;
export type FontSizes = typeof FONT_SIZES;
export type Breakpoints = typeof BREAKPOINTS;
export type BreakpointKeys = keyof typeof BREAKPOINTS;
export type Theme = typeof THEME;

export const useTheme = () => THEME;
